import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/material";
import {
  Menu,
  MenuItem,
  Sidebar,
  SubMenu,
  menuClasses,
} from "react-pro-sidebar";
import { Icon } from "../icons";
import { hexToRGBA } from "../../utils/hex-to-rgba";
import "./SidebarStyles.css";

const makePath = (basePath, path = "") =>
  path ? `${basePath}/${path}` : basePath;

const sidebarMenus = [
  {
    label: "홈",
    path: "",
    icon: "homeIcon",
  },
  {
    label: "위험성평가",
    icon: "performanceIcon",
    children: [
      // Wizard 직접 진입은 단계별 페이지 정리 전까지 사이드바에서 숨깁니다.
      // {
      //   label: "평가 Wizard",
      //   path: "assessment/wizard",
      //   icon: "todoIcon",
      // },
      {
        label: "STEP 1 대상/위험성평가",
        path: "assessment/wizard/step-1",
        icon: "squareSliderIcon",
      },
      {
        label: "STEP 2 개선대책",
        path: "assessment/wizard/step-2",
        icon: "editIcon",
      },
      {
        label: "STEP 3 완료 처리/승인",
        path: "assessment/wizard/step-3",
        icon: "checklistIcon",
      },
    ],
  },
  {
    label: "개선대책",
    icon: "trendUpIcon",
    children: [
      {
        label: "개선대책 등록",
        path: "strategy/register",
        icon: "addtaskIcon",
      },
      {
        label: "개선 완료 처리",
        path: "strategy/result",
        icon: "highlightIcon",
      },
    ],
  },
  {
    label: "현장 안전 관리",
    icon: "healthbagIcon",
    children: [
      {
        label: "TBM 관리",
        path: "monitoring/tbm",
        icon: "officeIcon",
      },
      {
        label: "안전점검 관리",
        path: "monitoring/inspection",
        icon: "viewIcon",
      },
      {
        label: "후속 조치 관리",
        path: "monitoring/actions",
        icon: "configFileIcon",
      },
      {
        label: "제안 검토/승인",
        path: "worker/hazard-review",
        icon: "messageIcon",
      },
      {
        label: "안전보건교육 관리",
        path: "monitoring/education",
        icon: "calendarIcon",
      },
      {
        label: "법정 안전관리",
        path: "monitoring/compliance",
        icon: "shieldIcon",
      },
      {
        label: "설비별 위험요인",
        path: "register/cause",
        icon: "fireIcon",
      },
    ],
  },
  {
    label: "기준정보",
    icon: "settingsIcon",
    children: [
      {
        label: "라인 관리",
        path: "standard/location",
        icon: "nodesIcon",
      },
      {
        label: "공정 관리",
        path: "standard/process",
        icon: "ganttIcon",
      },
      {
        label: "설비 관리",
        path: "standard/machine",
        icon: "repairIcon",
      },
      {
        label: "위험요인 관리",
        path: "standard/cause",
        icon: "warningIcon",
      },
      {
        label: "MSDS 관리",
        path: "standard/msds",
        icon: "folderIcon",
      },
      {
        label: "설비-공정 연결",
        path: "register/machine",
        icon: "squareSettingsIcon",
      },
      {
        label: "직원 정보",
        path: "standard/employee",
        icon: "usersIcon",
      },
      {
        label: "위험성 기준표",
        path: "standard/criteriaTable",
        icon: "kpiIcon",
      },
      {
        label: "안전보건 기준",
        path: "standard/safetyRegulations",
        icon: "shieldIcon",
      },
      {
        label: "비상 시나리오",
        path: "standard/scenario",
        icon: "sirenIcon",
      },
    ],
  },
  {
    label: "근로자 기능",
    icon: "workerIcon",
    children: [
      {
        label: "유해·위험요인 제안",
        path: "worker/hazard-proposal",
        icon: "qrIcon",
      },
      {
        label: "내 조치 업무",
        path: "worker/action-inbox",
        icon: "bellIcon",
      },
    ],
  },
  {
    label: "보고서",
    path: "monitoring/integrated-report",
    icon: "chartIcon",
  },
  {
    label: "조직관리",
    path: "organization",
    icon: "usersIcon",
  },
];

const visibleSafeMenuPaths = new Set([
  "",
  "standard/location",
  "standard/process",
  "standard/machine",
  "standard/cause",
  "standard/msds",
  "register/machine",
  "register/cause",
  // "assessment/wizard",
  "assessment/wizard/step-1",
  "assessment/wizard/step-2",
  "assessment/wizard/step-3",
  "monitoring/tbm",
  "monitoring/inspection",
  "monitoring/actions",
  "monitoring/education",
  "monitoring/compliance",
  "worker/hazard-proposal",
  "worker/action-inbox",
  "worker/hazard-review",
  "monitoring/integrated-report",
  "organization",
]);

const buildSidebarMenus = (visiblePaths) =>
  sidebarMenus
  .map((menu) => {
    if (!menu.children) {
      return visiblePaths.has(menu.path || "") ? menu : null;
    }

    const children = menu.children.filter((item) =>
      visiblePaths.has(item.path)
    );
    return children.length ? { ...menu, children } : null;
  })
  .filter(Boolean);

const SafeSidebar = ({
  isMobile = false,
  toggled = false,
  onToggle,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { group_code, project_code } = useParams();
  const user = useSelector((state) => state.auth.user);

  const basePath = useMemo(
    () => `/safe/${group_code}/${project_code}`,
    [group_code, project_code]
  );

  const isActive = (path = "") =>
    location.pathname === makePath(basePath, path);

  const isGroupActive = (children = []) =>
    children.some(
      (item) => location.pathname === makePath(basePath, item.path)
    );

  const handleNavigate = (path = "") => {
    navigate(makePath(basePath, path));

    if (isMobile) {
      onToggle?.(false);
    }
  };

  useEffect(() => {
    if (!isMobile || !toggled) {
      return;
    }

    requestAnimationFrame(() => {
      document
        .querySelector(".safe-sidebar-mobile .ps-sidebar-container")
        ?.scrollTo({ top: 0, left: 0 });
    });
  }, [isMobile, toggled]);

  const visibleSidebarMenus = useMemo(
    () => buildSidebarMenus(visibleSafeMenuPaths),
    []
  );

  if (!user) {
    return null;
  }

  const renderedSidebarMenus = visibleSidebarMenus;

  return (
    <Sidebar
      className={isMobile ? "safe-sidebar-mobile" : "safe-sidebar-desktop"}
      collapsed={false}
      toggled={toggled}
      onBackdropClick={() => onToggle?.(false)}
      breakPoint="md"
      customBreakPoint="899.95px"
      width="248px"
      rootStyles={{
        position: isMobile ? "fixed" : "relative",
        top: isMobile ? 0 : undefined,
        left: isMobile ? 0 : undefined,
        bottom: isMobile ? 0 : undefined,
        height: isMobile ? "100dvh" : "100%",
        borderRight: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        zIndex: isMobile ? theme.zIndex.appBar - 1 : theme.zIndex.drawer,
        "& .ps-sidebar-container": {
          height: isMobile ? "100dvh" : "100%",
          paddingTop: isMobile ? "64px" : 0,
          overflowY: "auto",
          backgroundColor: theme.palette.background.paper,
        },
        "& .ps-sidebar-backdrop": {
          top: isMobile ? "64px" : 0,
          height: isMobile ? "calc(100dvh - 64px)" : "100%",
        },
      }}
    >
      {/* <Box
        sx={{
          px: 2,
          py: 2.25,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 0, lineHeight: 1.2 }}
            >
              Risk Assessment
            </Typography>
            <Typography variant="subtitle1" fontWeight={800} noWrap>
              위험성평가
            </Typography>
          </Box>
          <Chip
            size="small"
            label="관리자"
            color="primary"
            variant="outlined"
            sx={{ height: 24, fontWeight: 700 }}
          />
        </Stack>
      </Box> */}

      <Menu
        closeOnClick={isMobile}
        menuItemStyles={{
          root: {
            fontSize: "0.9rem",
            fontWeight: 600,
          },
          button: ({ active, level }) => ({
            height: level === 0 ? 44 : 38,
            margin: level === 0 ? "4px 10px" : "2px 10px 2px 18px",
            paddingLeft: level === 0 ? 12 : 16,
            paddingRight: 12,
            borderRadius: 8,
            color: active
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
            backgroundColor: active
              ? hexToRGBA(theme.palette.primary.main, 0.12)
              : "transparent",
            [`&.${menuClasses.active}`]: {
              color: theme.palette.primary.main,
              backgroundColor: hexToRGBA(theme.palette.primary.main, 0.12),
              fontWeight: 800,
            },
            "&:hover": {
              color: theme.palette.primary.main,
              backgroundColor: hexToRGBA(theme.palette.primary.main, 0.08),
            },
          }),
          icon: ({ active }) => ({
            minWidth: 34,
            color: active
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
          }),
          label: {
            whiteSpace: "normal",
            lineHeight: 1.25,
          },
          subMenuContent: {
            backgroundColor: theme.palette.background.paper,
          },
          SubMenuExpandIcon: ({ open }) => ({
            color: open
              ? theme.palette.primary.main
              : theme.palette.text.disabled,
          }),
        }}
      >
        {renderedSidebarMenus.map((menu) =>
          menu.children ? (
            <SubMenu
              key={menu.label}
              label={menu.label}
              icon={<Icon name={menu.icon} width={18} height={18} />}
              defaultOpen={isGroupActive(menu.children)}
              active={isGroupActive(menu.children)}
            >
              {menu.children.map((item) => (
                <MenuItem
                  key={item.path}
                  icon={<Icon name={item.icon} width={16} height={16} />}
                  active={isActive(item.path)}
                  onClick={() => handleNavigate(item.path)}
                >
                  {item.label}
                </MenuItem>
              ))}
            </SubMenu>
          ) : (
            <MenuItem
              key={menu.label}
              icon={<Icon name={menu.icon} width={18} height={18} />}
              active={isActive(menu.path)}
              onClick={() => handleNavigate(menu.path)}
            >
              {menu.label}
            </MenuItem>
          )
        )}
      </Menu>
    </Sidebar>
  );
};

export default SafeSidebar;
